import { HeatLegend, IHeatLegendSettings, Label, color, p50, percent } from "@amcharts/amcharts5";
import { AxisRendererX, AxisRendererY, CategoryAxis, ColumnSeries, XYChart } from "@amcharts/amcharts5/xy";

import BaseChart from "@/classes/BaseChart";
import Lang from "@/lang";
import { chartFontSettings, chartInitialSettings } from "@/settings/widgets";
import { ChartDataRecord, TooltipSeries } from "@/types/charts";

export default class HeatMap extends BaseChart {
  xAxisData!: Record<string, unknown>[];
  yAxisData!: Record<string, unknown>[];

  initChart() {
    this.root.container.children.clear();

    if (this.chart) {
      (this.chart as XYChart).series.clear();
    }

    this.createChart();

    this.createRenderers();
    this.createAxes();

    const series = [this.settings.decompositionMetric];
    series.forEach((name) => {
      if (typeof this.createSeries === "function") {
        this.createSeries(name);
      }
    });
  }

  protected createChart() {
    this.chart = this.root.container.children.push(
      XYChart.new(this.root, {
        ...chartInitialSettings,
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        layout: this.layout,
      })
    );
  }

  protected createRenderers() {
    const x = AxisRendererX.new(this.root, {
      visible: false,
      minGridDistance: 200,
      cellStartLocation: 0.05,
      cellEndLocation: 0.95,
    });
    x.grid.template.set("visible", false);

    const y = AxisRendererY.new(this.root, {
      visible: false,
      minGridDistance: 40,
      cellStartLocation: 0.05,
      cellEndLocation: 0.95,
      inversed: true,
    });
    y.grid.template.set("visible", false);

    this.renderers = { x, y };
  }

  protected createAxes() {
    const x = (this.chart as XYChart).xAxes.push(
      CategoryAxis.new(this.root, {
        renderer: this.renderers.x as AxisRendererX,
        categoryField: this.settings.decompositionMetricSecondary,
      })
    );
    x.data.setAll(this.xAxisData);
    if (this.settings.decompositionMetricSecondaryTitle) {
      x.children.push(
        Label.new(this.root, {
          text: this.settings.decompositionMetricSecondaryTitle,
          ...chartFontSettings.axisTitle,
          x: p50,
          marginTop: 0,
          centerX: p50,
        })
      );
    }

    const y = (this.chart as XYChart).yAxes.push(
      CategoryAxis.new(this.root, {
        maxDeviation: 0,
        renderer: this.renderers.y as AxisRendererY,
        categoryField: this.settings.decompositionMetric,
      })
    );
    y.data.setAll(this.yAxisData);
    if (this.settings.decompositionMetricTitle) {
      y.children.unshift(
        Label.new(this.root, {
          text: this.settings.decompositionMetricTitle,
          ...chartFontSettings.axisTitle,
          rotation: -90,
          y: percent(50),
          centerX: p50,
        })
      );
    }

    this.axes = { x, y };
  }

  protected createSeries(name: string) {
    const series = (this.chart as XYChart).series.push(
      ColumnSeries.new(this.root, {
        name,
        calculateAggregates: true,
        clustered: false,
        xAxis: this.axes.x,
        yAxis: this.axes.y,
        categoryXField: this.settings.decompositionMetricSecondary,
        categoryYField: this.settings.decompositionMetric,
        valueField: this.settings.displayMetric,
      })
    );

    series.columns.template.setAll({
      tooltipText: "{value}",
      strokeOpacity: 0,
      width: percent(100),
      height: percent(100),
    });

    series.set("heatRules", [
      {
        target: series.columns.template,
        min: color(this.settings.minValueColor),
        max: color(this.settings.maxValueColor),
        dataField: "value",
        key: "fill",
      },
    ]);

    this.createTooltip(series as TooltipSeries, (target) => {
      let param: Record<string, unknown>[] = [];
      const tooltipValues = target?.dataItem?.dataContext as Record<string, unknown>;
      if (tooltipValues) {
        param = [
          {
            key: this.settings.decompositionMetricSecondary,
            value: tooltipValues[this.settings.decompositionMetricSecondary],
          },
          {
            key: this.settings.decompositionMetric,
            value: tooltipValues[this.settings.decompositionMetric],
          },
          { key: this.settings.displayMetric, value: tooltipValues[this.settings.displayMetric] },
        ];
      }
      return param;
    });

    this.createLegend(series);

    series.data.setAll(this.data);
  }

  createLegend(s: ColumnSeries) {
    if (!this.settings.legendPosition) {
      return;
    }

    const defaultSettingX = {
      centerX: p50,
      x: p50,
      marginTop: 0,
      layout: this.root.gridLayout,
      orientation: "horizontal",
    };

    const defaultSettingY = {
      centerY: p50,
      y: percent(50),
      marginRight: 30,
      layout: this.root.verticalLayout,
      orientation: "vertical",
    };

    let legendSetting;

    switch (this.settings.legendPosition) {
      case "left": {
        legendSetting = defaultSettingY;
        break;
      }
      case "right": {
        legendSetting = { ...defaultSettingY, marginLeft: 40 };
        break;
      }
      case "top":
        legendSetting = { ...defaultSettingX, marginBottom: 30 };
        break;
      case "bottom":
        legendSetting = defaultSettingX;
        break;
      default:
        legendSetting = defaultSettingX;
        console.error(`Bad legend position '${this.settings.legendPosition}' for chart '${this.chartName}'.`);
    }

    legendSetting = {
      ...legendSetting,
      startColor: color(this.settings.minValueColor),
      endColor: color(this.settings.maxValueColor),
    };

    let heatLegend: HeatLegend;

    switch (this.settings.legendPosition) {
      case "top":
        heatLegend = (this.chart as XYChart).children.unshift(
          HeatLegend.new(this.root, legendSetting as IHeatLegendSettings)
        );
        break;
      case "left":
        heatLegend = (this.chart as XYChart).children.unshift(
          HeatLegend.new(this.root, legendSetting as IHeatLegendSettings)
        );
        break;
      case "right":
        heatLegend = (this.chart as XYChart).children.push(
          HeatLegend.new(this.root, legendSetting as IHeatLegendSettings)
        );
        break;
      default:
        heatLegend = (this.chart as XYChart).bottomAxesContainer.children.push(
          HeatLegend.new(this.root, legendSetting as IHeatLegendSettings)
        );
    }

    const formatter = this.formatters[this.settings.displayMetric];

    s.columns.template.events.on("pointerover", (event) => {
      const { dataItem } = event.target;

      if (dataItem) {
        const val = (dataItem.dataContext as Record<string, number>)[this.settings.displayMetric];

        heatLegend.showValue(
          val,
          this.settings.compactFormat ? formatter(val, { notation: "compact" }) : formatter(val)
        );
      }
    });

    s.columns.template.events.on("pointerout", () => {
      heatLegend.hideTooltip();
    });

    s.events.on("datavalidated", () => {
      const valueLow = s.getPrivate("valueLow");
      const valueHigh = s.getPrivate("valueHigh");

      heatLegend.set("startValue", valueLow);
      heatLegend.set(
        "startText",
        this.settings.compactFormat ? formatter(valueLow, { notation: "compact" }) : formatter(valueLow)
      );

      heatLegend.set("endValue", valueHigh);
      heatLegend.set(
        "endText",
        this.settings.compactFormat ? formatter(valueHigh, { notation: "compact" }) : formatter(valueHigh)
      );
    });
  }

  protected prepareAxisData(data: Record<string, unknown>[], field: string): Record<string, unknown>[] {
    return (data as Record<string, string>[])
      .reduce((acc: string[], item) => (acc.includes(item[field]) ? acc : [...acc, item[field]]), [])
      .map((item: string) => ({
        [field]: item,
      }));
  }

  fillZeroValues(data: Record<string, unknown>[]): Record<string, unknown>[] {
    this.xAxisData.forEach((xAxisItem) => {
      this.yAxisData.forEach((yAxisItem) => {
        const existingItem = data.find(
          (item: Record<string, unknown>) =>
            item[this.settings.decompositionMetricSecondary] ===
              xAxisItem[this.settings.decompositionMetricSecondary] &&
            item[this.settings.decompositionMetric] === yAxisItem[this.settings.decompositionMetric]
        );

        if (!existingItem) {
          data.push({
            [this.settings.decompositionMetricSecondary]: xAxisItem[this.settings.decompositionMetricSecondary],
            [this.settings.decompositionMetric]: yAxisItem[this.settings.decompositionMetric],

            [this.settings.displayMetric]: 0,
          });
        }
      });
    });

    return data;
  }

  protected mapData(data: ChartDataRecord[]) {
    let prepared: ChartDataRecord[] = data.map((item) => ({
      [this.settings.decompositionMetricSecondary]: item[this.settings.decompositionMetricSecondary] ?? Lang.null,
      [this.settings.decompositionMetric]: item[this.settings.decompositionMetric] ?? Lang.null,

      [this.settings.displayMetric]: item[this.settings.displayMetric] ?? 0,
    }));

    this.xAxisData = this.prepareAxisData(prepared, this.settings.decompositionMetricSecondary);
    this.yAxisData = this.prepareAxisData(prepared, this.settings.decompositionMetric);

    prepared = this.fillZeroValues(prepared);

    // TODO: Maybe show items will appear here later.
    return prepared;
  }
}
